import { lazy, useState, useEffect, Suspense } from 'react';
import styled from 'styled-components';
import type { Darwin } from '@kiwicom/account-web/dataFetching/getDarwinConfig';

import Button from '../Buttons/Button';
import Modal from '../Modal';
import Loading from '../Loading';

const ModalContent = lazy(() => import('./DebugModalContent'));
const Wrapper = styled.div`
  position: fixed;
  bottom: 30px;
  right: 30px;
  z-index: 1000;
`;
type Props = {
  isDebugEnabled: boolean;
  gqlEndpoint: 'SANDBOX' | 'PRODUCTION' | null;
  darwinConfig: Darwin;
};
export enum Tab {
  DARWIN = 'DARWIN',
  LOCALISATION = 'LOCALISATION',
  TOOLS = 'TOOLS',
  FORMS = 'FORMS',
}

function areKeysPressed(keysPressed: Set<string>, keys = []) {
  const required = new Set(keys);

  for (const elem of keysPressed) {
    required.delete(elem);
  }

  return required.size === 0;
}

function useMultiKeyPress(): Set<string> {
  const [keysPressed, setKeyPressed] = useState(new Set([]));

  const downHandler = ({ key }) => {
    setKeyPressed((state) => {
      const newState = new Set(state);
      newState.add(key);
      return newState;
    });
  };

  const upHandler = ({ key }) => {
    setKeyPressed((state) => {
      const newState = new Set(state);
      newState.delete(key);
      return newState;
    });
  };

  useEffect(() => {
    window.addEventListener('keydown', downHandler);
    window.addEventListener('keyup', upHandler);
    return () => {
      window.removeEventListener('keydown', downHandler);
      window.removeEventListener('keyup', upHandler);
    };
  }, []);
  return keysPressed;
}

const DebugModal = ({ isDebugEnabled, gqlEndpoint, darwinConfig }: Props): JSX.Element | null => {
  const [showModal, toggleShowModal] = useState(false);
  const [tab, toggleTab] = useState<Tab>(Tab.DARWIN);
  const [debugEnabled, setDebugEnabled] = useState(isDebugEnabled);
  const keysPressed = useMultiKeyPress();
  const debugPressed = areKeysPressed(keysPressed, ['Control', 'Alt', '∂']);
  useEffect(() => {
    if (debugPressed && !showModal) {
      setDebugEnabled(true);
      toggleShowModal(true);
    }
  }, [debugPressed, showModal, setDebugEnabled]);

  if (!debugEnabled) {
    return null;
  }

  return (
    <>
      <Wrapper>
        <Button onClick={() => toggleShowModal(true)} type="secondary" size="normal">
          Debug window
        </Button>
      </Wrapper>
      <Modal isVisible={showModal} size="small" onClose={() => toggleShowModal(false)}>
        <Suspense fallback={<Loading />}>
          <ModalContent
            tab={tab}
            gqlEndpoint={gqlEndpoint}
            toggleShowModal={toggleShowModal}
            toggleTab={toggleTab}
            darwinConfig={darwinConfig}
          />
        </Suspense>
      </Modal>
    </>
  );
};

export default DebugModal;
